import { logError } from "@/lib/log";
import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Check, Crown, Infinity as InfinityIcon } from "lucide-react";
import { openRazorpayCheckout } from "@/lib/razorpay";

export const PLANS = [
  {
    id: "free",
    name: "Free",
    price: "₹0",
    period: "forever",
    features: ["Unlimited transactions", "Budgets & accounts", "Bills & recurring rules", "Basic reports & CSV export"],
  },
  {
    id: "premium_monthly",
    name: "Premium Monthly",
    price: "₹99",
    period: "per month",
    features: ["Everything in Free", "AI assistant & voice logging", "AI spending insights", "Advanced reports & charts", "Priority support"],
  },
  {
    id: "lifetime",
    name: "Lifetime",
    price: "₹2,499",
    period: "one-time",
    badge: "Best value",
    features: ["Everything in Premium", "Pay once, keep forever", "All future features included", "International payments (coming soon)"],
  },
];

function PlanStatusBanner({ user }) {
  const plan = user?.effective_plan || "free";
  if (plan === "free") return null;
  const isLifetime = plan === "lifetime";
  const isTrial = !isLifetime && user?.trial;
  let heading = "Premium active.";
  let detail = `Valid until ${formatDate(user.premium_until)}`;
  if (isLifetime) {
    heading = "Lifetime license active.";
    detail = "Never expires — thank you for the support.";
  } else if (isTrial) {
    heading = "Free Premium trial active.";
    detail = `${user.trial_days_left ?? 0} day${user.trial_days_left === 1 ? "" : "s"} left — upgrade below to keep AI features after your trial ends.`;
  }
  return (
    <div className="border border-[#A67C00] bg-[#FDFBF7] p-6 flex items-center gap-4" data-testid="plan-status-banner">
      {isLifetime ? <InfinityIcon className="w-6 h-6 text-[#A67C00]" /> : <Crown className="w-6 h-6 text-[#A67C00]" />}
      <div>
        <div className="font-serif text-2xl tracking-tight">{heading}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565] mt-1" data-testid="plan-expiry">
          {detail}
        </div>
      </div>
    </div>
  );
}

function PlanCardAction({ plan, isCurrent, buyable, payingId, onBuy }) {
  if (isCurrent) {
    return (
      <div className="border border-[#2E6F40] text-[#2E6F40] py-3 text-center text-xs uppercase tracking-[0.2em]" data-testid={`current-plan-${plan.id}`}>Current plan</div>
    );
  }
  if (buyable) {
    return (
      <button
        data-testid={`buy-now-${plan.id}`}
        onClick={() => onBuy(plan.id)}
        disabled={payingId === plan.id}
        className="w-full bg-[#1A1F16] text-[#F7F5F0] py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#A67C00] disabled:opacity-50 transition-colors"
      >
        {payingId === plan.id ? "Opening…" : "Buy now"}
      </button>
    );
  }
  return (
    <div className="border border-[#D8D4C7] text-[#6C7565] py-3 text-center text-xs uppercase tracking-[0.2em]">
      {plan.id === "free" ? "Included" : "Covered by Lifetime"}
    </div>
  );
}

function PlanCard({ plan, currentPlan, payingId, onBuy }) {
  const isCurrent =
    (plan.id === "free" && currentPlan === "free") ||
    (plan.id === "premium_monthly" && currentPlan === "premium") ||
    (plan.id === "lifetime" && currentPlan === "lifetime");
  const buyable = plan.id !== "free" && currentPlan !== "lifetime";
  return (
    <div className={`relative border-2 bg-white p-8 flex flex-col ${plan.badge ? "border-[#A67C00] shadow-[6px_6px_0px_#A67C00]" : "border-[#1A1F16] shadow-[6px_6px_0px_#1A1F16]"}`} data-testid={`plan-card-${plan.id}`}>
      {plan.badge && (
        <span className="absolute -top-3 left-6 bg-[#A67C00] text-white font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1">{plan.badge}</span>
      )}
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6C7565]">{plan.name}</div>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-serif text-5xl tracking-tight num">{plan.price}</span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">{plan.period}</span>
      </div>
      <ul className="mt-6 space-y-2.5 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check className="w-4 h-4 text-[#2E6F40] shrink-0 mt-0.5" /> {f}
          </li>
        ))}
      </ul>
      <div className="mt-8">
        <PlanCardAction plan={plan} isCurrent={isCurrent} buyable={buyable} payingId={payingId} onBuy={onBuy} />
      </div>
    </div>
  );
}

export default function Pricing() {
  const { user, refresh } = useAuth();
  const [payingId, setPayingId] = useState(null);
  const currentPlan = user?.effective_plan || "free";

  const buy = async (planId) => {
    setPayingId(planId);
    try {
      const { data: order } = await api.post("/payments/premium/order", { plan_id: planId });
      openRazorpayCheckout({
        order,
        description: order.plan_label,
        user,
        onSettled: () => setPayingId(null),
        onSuccess: async (res) => {
          try {
            const { data } = await api.post("/payments/premium/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success(
              data.premium_until
                ? `Premium activated — valid until ${formatDate(data.premium_until)}`
                : "Lifetime license activated — never expires"
            );
            await refresh();
          } catch (e) {
            logError("Premium verification failed", e);
            toast.error("Payment verification failed — contact support with your payment ID");
          } finally {
            setPayingId(null);
          }
        },
      });
    } catch (e) {
      logError("Premium order creation failed", e);
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setPayingId(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="border-b border-[#1A1F16] pb-6">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Classifieds · Plans</span>
        <h2 className="font-serif text-5xl tracking-tight mt-2">Pricing.</h2>
        <p className="font-serif text-lg text-[#6C7565] mt-2 max-w-2xl">
          Start free. Upgrade when the ledger earns its keep. Payments via Razorpay — UPI, cards, netbanking.
        </p>
      </div>

      <PlanStatusBanner user={user} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-2">
        {PLANS.map((p) => (
          <PlanCard key={p.id} plan={p} currentPlan={currentPlan} payingId={payingId} onBuy={buy} />
        ))}
      </div>

      <p className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">
        Prices in INR, inclusive of taxes · Secured by Razorpay · Premium activates instantly after payment
      </p>
    </div>
  );
}
